import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

type UseFeatureListScrollStateOptions = {
  itemCount: number;
};

type UseFeatureListScrollStateReturn = {
  leftColumnRef: MutableRefObject<HTMLDivElement | null>;
  registerCard: (index: number, node: HTMLDivElement | null) => void;
  activeIndex: number;
  previousIndex: number;
  anchorOffset: number;
  scrollBuffer: number;
};

export function useFeatureListScrollState({
  itemCount
}: UseFeatureListScrollStateOptions): UseFeatureListScrollStateReturn {
  const leftColumnRef = useRef<HTMLDivElement | null>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const frameRef = useRef<number | null>(null);
  const anchorOffsetRef = useRef<number | null>(null);
  const anchorOffsetStateRef = useRef(0);
  const activeIndexRef = useRef(0);
  const cardObserverRef = useRef<ResizeObserver | null>(null);
  const leftObserverRef = useRef<ResizeObserver | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(0);
  const [anchorOffset, setAnchorOffset] = useState(0);
  const [scrollBuffer, setScrollBuffer] = useState(0);
  const resetAnchorOffset = useCallback(() => {
    anchorOffsetRef.current = null;
    anchorOffsetStateRef.current = 0;
    setAnchorOffset(0);
    setScrollBuffer(0);
  }, []);

  const measureAnchorViewport = useCallback(() => {
    if (typeof window === 'undefined') {
      return 0;
    }

    const column = leftColumnRef.current;
    const firstCard = cardsRef.current[0];

    if (!column) {
      return window.innerHeight * 0.35;
    }

    const columnRect = column.getBoundingClientRect();
    if (anchorOffsetRef.current == null) {
      if (firstCard) {
        const cardRect = firstCard.getBoundingClientRect();
        const columnHeight = columnRect.height;
        const cardCenterOffset =
          cardRect.top - columnRect.top + cardRect.height / 2;
        const boundedOffset = Number.isFinite(columnHeight)
          ? Math.max(0, Math.min(columnHeight, cardCenterOffset))
          : Math.max(0, cardCenterOffset);

        anchorOffsetRef.current = boundedOffset;
      } else {
        anchorOffsetRef.current = Math.max(0, columnRect.height * 0.25);
      }
    }

    if (anchorOffsetRef.current != null) {
      const roundedOffset = Math.round(anchorOffsetRef.current);
      if (roundedOffset !== anchorOffsetStateRef.current) {
        anchorOffsetStateRef.current = roundedOffset;
        setAnchorOffset(roundedOffset);
      }
    }

    return columnRect.top + (anchorOffsetRef.current ?? 0);
  }, []);

  const recomputeScrollBuffer = useCallback(() => {
    if (typeof window === 'undefined') {
      setScrollBuffer(0);
      return;
    }

    const column = leftColumnRef.current;
    const lastCard = cardsRef.current[itemCount - 1];

    if (!column || !lastCard) {
      setScrollBuffer(0);
      return;
    }

    measureAnchorViewport();

    const anchorOffsetValue = anchorOffsetRef.current ?? 0;
    const computedTopValue = Number.parseFloat(
      window.getComputedStyle(column).top ?? '0'
    );
    const stickyTop = Number.isFinite(computedTopValue) ? computedTopValue : 0;
    const viewportHeight = window.innerHeight;
    const lastCardHeight = lastCard.offsetHeight;

    if (viewportHeight <= 0 || lastCardHeight <= 0) {
      setScrollBuffer(0);
      return;
    }

    const anchorViewport = stickyTop + anchorOffsetValue;
    const rawBuffer = viewportHeight - anchorViewport - lastCardHeight / 2;
    const overshootPadding = 100;
    const safeBuffer = Math.max(0, Math.round(rawBuffer - overshootPadding));
    setScrollBuffer(safeBuffer);
  }, [itemCount, measureAnchorViewport]);

  const updateActiveIndex = useCallback(() => {
    frameRef.current = null;

    if (itemCount === 0 || typeof window === 'undefined') {
      return;
    }

    const anchorViewport = measureAnchorViewport();
    const tolerance = 2;
    let coveringIndex: number | null = null;
    let closestIndex = activeIndexRef.current;
    let closestDistance = Number.POSITIVE_INFINITY;

    cardsRef.current.forEach((card, index) => {
      if (!card) {
        return;
      }

      const rect = card.getBoundingClientRect();
      const cardCenter = rect.top + rect.height / 2;

      if (
        rect.top - tolerance <= anchorViewport &&
        rect.bottom + tolerance >= anchorViewport
      ) {
        coveringIndex = index;
      }

      const distance = Math.abs(cardCenter - anchorViewport);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    let nextIndex = coveringIndex ?? closestIndex;
    const lastCard = cardsRef.current[itemCount - 1];
    const column = leftColumnRef.current;

    if (lastCard && column) {
      const rail = lastCard.parentElement as HTMLDivElement | null;

      if (rail) {
        const tolerancePx = 2;
        const columnRect = column.getBoundingClientRect();
        const computedTopValue = Number.parseFloat(
          window.getComputedStyle(column).top ?? '0'
        );
        const stickyTop = Number.isFinite(computedTopValue)
          ? computedTopValue
          : 0;
        const isSticky = columnRect.top <= stickyTop + tolerancePx;

        if (isSticky) {
          const railRect = rail.getBoundingClientRect();

          if (railRect.height > tolerancePx) {
            const viewportBottom = window.scrollY + window.innerHeight;
            const railBottom =
              window.scrollY + railRect.top + rail.scrollHeight;
            const secondLastIndex = itemCount - 2;
            const secondLastIsActive =
              itemCount < 2 || activeIndexRef.current >= secondLastIndex;

            if (
              viewportBottom >= railBottom - tolerancePx &&
              secondLastIsActive
            ) {
              nextIndex = itemCount - 1;
            }
          }
        }
      }
    }

    const clampedIndex = Math.max(0, Math.min(itemCount - 1, nextIndex));

    if (clampedIndex !== activeIndexRef.current) {
      setPreviousIndex(activeIndexRef.current);
      activeIndexRef.current = clampedIndex;
      setActiveIndex(clampedIndex);
    }
  }, [itemCount, measureAnchorViewport]);

  const scheduleUpdate = useCallback(() => {
    if (frameRef.current != null) {
      return;
    }

    frameRef.current = requestAnimationFrame(updateActiveIndex);
  }, [updateActiveIndex]);

  useEffect(() => {
    cardsRef.current = Array.from(
      { length: itemCount },
      (_, index) => cardsRef.current[index] ?? null
    );
    activeIndexRef.current = 0;
    setActiveIndex(0);
    setPreviousIndex(0);
    resetAnchorOffset();
    scheduleUpdate();
    recomputeScrollBuffer();
  }, [itemCount, resetAnchorOffset, scheduleUpdate, recomputeScrollBuffer]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    return () => {
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      resetAnchorOffset();
      scheduleUpdate();
      recomputeScrollBuffer();
    };

    const handleScroll = () => {
      scheduleUpdate();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [resetAnchorOffset, scheduleUpdate, recomputeScrollBuffer]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const cardObserver = new ResizeObserver((entries) => {
      if (entries.some((entry) => entry.target === cardsRef.current[0])) {
        resetAnchorOffset();
      }
      scheduleUpdate();
      recomputeScrollBuffer();
    });

    const leftObserver = new ResizeObserver(() => {
      resetAnchorOffset();
      scheduleUpdate();
      recomputeScrollBuffer();
    });

    cardObserverRef.current = cardObserver;
    leftObserverRef.current = leftObserver;

    cardsRef.current.forEach((node) => {
      if (node) {
        cardObserver.observe(node);
      }
    });

    if (leftColumnRef.current) {
      leftObserver.observe(leftColumnRef.current);
    }

    return () => {
      cardObserver.disconnect();
      leftObserver.disconnect();

      if (cardObserverRef.current === cardObserver) {
        cardObserverRef.current = null;
      }

      if (leftObserverRef.current === leftObserver) {
        leftObserverRef.current = null;
      }
    };
  }, [resetAnchorOffset, scheduleUpdate, recomputeScrollBuffer]);

  const registerCard = useCallback(
    (index: number, node: HTMLDivElement | null) => {
      const previousNode = cardsRef.current[index];

      if (previousNode && cardObserverRef.current) {
        cardObserverRef.current.unobserve(previousNode);
      }

      cardsRef.current[index] = node;

      if (index === 0) {
        resetAnchorOffset();
      }

      if (!node) {
        scheduleUpdate();
        recomputeScrollBuffer();
        return;
      }

      if (cardObserverRef.current) {
        cardObserverRef.current.observe(node);
      }

      scheduleUpdate();
      recomputeScrollBuffer();
    },
    [resetAnchorOffset, scheduleUpdate, recomputeScrollBuffer]
  );

  return {
    leftColumnRef,
    registerCard,
    activeIndex,
    previousIndex,
    anchorOffset,
    scrollBuffer
  };
}
